import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from "@reduxjs/toolkit";
import { ViewModeItem } from "../models/ViewModeItem";
import { ViewModeItems } from "../models/ViewModeItem";
import { SortItem, AvailableSortItems } from '../models/SortItem';
import { LookupItem } from "../models/LookupItem";
import { FacetResult } from "../models/FacetResult";
import { NewsItem } from "../models/news/NewsItem";
import { SuggestItem } from '../models/SuggestItem';
import { SuggestFilter } from '../models/common/SuggestFilter';

export interface NewsState {
  viewMode: ViewModeItem;
  orderBy: undefined | SortItem;
  keywords: string;
  suggestions: SuggestItem[];
  isLoading: boolean;
  canLoadMore: boolean;
  isSuggestLoading: boolean;
  items: NewsItem[];
  pageSize: number;
  page: number;
  initDone: boolean;
}

const initialState: NewsState = {
  viewMode: ViewModeItems[0],
  orderBy: AvailableSortItems[2],
  keywords: "",
  suggestions: [],
  isLoading: false,
  canLoadMore: false,
  isSuggestLoading: false,
  items: [],
  pageSize: 9,
  page: 1,
  initDone: false,
};

export type SearchFilter = {
  keywords?: string;
  sortBy?: string;
  sortAsc?: boolean;
  path?: string;
  page: number;
  pageSize: number;
};

export const searchNews = createAsyncThunk<
  FacetResult<NewsItem>,
  SearchFilter
>(
  "data/searchNews",
  async (payload: SearchFilter): Promise<FacetResult<NewsItem>> => {
    const response = await fetch("/newssearch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
    return (await response.json()) as FacetResult<NewsItem>;
  }
);

export const searchNewsSuggest = createAsyncThunk<SuggestItem[], SuggestFilter>(
  "data/searchNewsSuggest",
  async (payload: SuggestFilter): Promise<SuggestItem[]> => {
    const response = await fetch("/newssearch/suggest", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
    return (await response.json()) as SuggestItem[];
  }
);

export const newsSlice = createSlice({
  name: "news",
  initialState,
  reducers: {
    changePage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    changeViewMode: (state, action: PayloadAction<ViewModeItem>) => {
      state.viewMode = action.payload;
    },
    changeKeywords: (state, action: PayloadAction<string>) => {
      if (state.keywords !== action.payload)
      {
        state.items = [];
        state.canLoadMore = false;
      }
      state.keywords = action.payload;
      state.page = 1;
    },
    changeOrder: (state, action: PayloadAction<undefined | SortItem>) => {
      if (state.orderBy?.Title !== action.payload?.Title) {
        state.items = [];
        state.canLoadMore = false;
      }
      state.orderBy = action.payload;
      state.page = 1;
    },
    changeInit: (state, action: PayloadAction<boolean>) => {
      state.initDone = action.payload;
    },
    clearSuggestions: (state) => {
      state.suggestions = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchNews.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        searchNews.fulfilled,
        (state, action: PayloadAction<FacetResult<NewsItem>>) => {
          state.isLoading = false;

          if (action.payload) {
            if (state.page == 1) {
              state.items = [];
            }
            if (action.payload.hits.length > 0) {
              state.items.push(...action.payload.hits);
            }

            state.canLoadMore =
              action.payload.totalHits > state.items.length &&
              state.items.length > 0;
          }
        }
      )
      .addCase(searchNews.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(searchNewsSuggest.pending, (state) => {
        state.isSuggestLoading = true;
      })
      .addCase(
        searchNewsSuggest.fulfilled,
        (state, action: PayloadAction<SuggestItem[]>) => {
          state.isSuggestLoading = false;

          state.suggestions = [];

          if (action.payload.length > 0) {
            state.suggestions = [...action.payload];
          }
        }
      )
      .addCase(searchNewsSuggest.rejected, (state) => {
        state.isSuggestLoading = false;
      });
  },
});

export const {
  changePage,
  changeViewMode,
  changeKeywords,
  changeOrder,
  changeInit,
  clearSuggestions
} = newsSlice.actions;

export default newsSlice.reducer;
